import { useState } from 'react'

import { DropdownMenuItem, DropdownMenuSeparator } from '@components/atoms/DropdownMenu'
import Icon from '@components/utils/Icon'
import { ConfirmDialog } from '@components/molecules/ConfirmDialog'
import { DotsMenu } from '@components/molecules/DotsMenu'
import AppErrorsAlert from '@molecules/Alerts/AppErrorsAlert'

import { scopedTranslation } from '@utils/I18n'
import { ShiftPublishState } from '@frontend/graphql/types.generated'

import { useShiftAuthoring } from './ShiftAuthoringContext'
import { useAddShiftsMutation } from './useAddShiftsMutation'
import { useArchiveShiftsMutation } from './useArchiveShiftsMutation'
import { EditShiftDialog } from './EditShiftDialog'
import { MoveShiftDialog } from './MoveShiftDialog'

import useRevertShiftChangesMutation from '../roster_board/useRevertShiftChangesMutation'

import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('author_shifts')

type ShiftActionsMenuProps = {
  shift: ShiftWithSlots
}

export function ShiftActionsMenu({ shift }: ShiftActionsMenuProps) {
  const { eventId, onChange } = useShiftAuthoring()
  const { addShifts } = useAddShiftsMutation()
  const { archiveShifts, isPending: isDeleting } = useArchiveShiftsMutation()
  const revertShiftChanges = useRevertShiftChangesMutation()
  const [editing, setEditing] = useState(false)
  const [moving, setMoving] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const assignedCount = shift.shiftSlots.assignedCount
  const hasUnpublishedChanges = shift.publishState === ShiftPublishState.UnpublishedChanges

  const handleRevert = () => revertShiftChanges({ id: shift.id, onSuccess: onChange })

  const handleDuplicate = () =>
    addShifts({
      eventId,
      shifts: [
        {
          roleName: shift.roleName,
          startTime: shift.startTime,
          endTime: shift.endTime,
          volunteersRequired: shift.shiftSlots.totalCount,
          draft: true,
          allowSelfSelect: shift.allowSelfSelect,
        },
      ],
      onSuccess: onChange,
    })

  const handleDelete = () => {
    archiveShifts({
      ids: [shift.id],
      onSuccess: () => {
        setConfirmingDelete(false)
        onChange()
      },
    })
  }

  return (
    <>
      <DotsMenu ariaLabel={t('actions_for', { role: shift.roleName })}>
        <DropdownMenuItem onSelect={() => setEditing(true)}>
          <Icon type="pencil-02" /> {t('edit')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleDuplicate}>
          <Icon type="copy-06" /> {t('duplicate')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setMoving(true)}>
          <Icon type="calendar" /> {t('move')}
        </DropdownMenuItem>
        {hasUnpublishedChanges && (
          <DropdownMenuItem onSelect={handleRevert}>
            <Icon type="flip-backwards" /> {t('revert_all')}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => setConfirmingDelete(true)}>
          <Icon type="trash-01" /> {t('delete')}
        </DropdownMenuItem>
      </DotsMenu>

      {editing && <EditShiftDialog open={editing} onOpenChange={setEditing} shift={shift} />}
      {moving && <MoveShiftDialog open={moving} onOpenChange={setMoving} shift={shift} />}
      {confirmingDelete && (
        <ConfirmDialog
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          title={t('delete_title')}
          body={
            assignedCount > 0
              ? t('delete_body', { role: shift.roleName, count: assignedCount })
              : t('delete_body_empty', { role: shift.roleName })
          }
          confirmLabel={t('delete_confirm')}
          confirmIcon="trash-01"
          onConfirm={handleDelete}
          busy={isDeleting}
        >
          <AppErrorsAlert errorsPath="rosterAssignment" />
        </ConfirmDialog>
      )}
    </>
  )
}
